import {ObjectId} from 'bson';
import {AccountWithLogo, AccountWithLogoSchema} from '../../scoredAccounts';
import {ObjectIdSchema} from '../../zod';
import {
  ApiDefinition,
  BatchedApiDefinition,
  WithIdSchema,
} from '../definitions';
import {z} from 'zod';
import {
  AccountActionPayloadSchema,
  AccountActionQueryPayloadSchema,
  AccountLinksRequestSchema,
  AccountLinkSchema,
  AccountQuerySchema,
  AccountSelectionSchema,
  DownloadRequestSchema,
  ExportRequest,
  AccountQueryWithoutSortSchema,
} from '../api';
import {AccountEnrichmentCreditRedemptionSchema} from '../../customer';
import {
  LegacyAiTeamSizeParamsSchema,
  FieldDefinitionAnalysisRunSchema,
  EnrichmentModeSchema,
} from '../../fields/enrichment';
import {
  AccountSimilarityScoreResponseSchema,
  CreditPriceResponseSchema,
} from '../responses';
import {AccountSimilarityScoreRequestSchema} from '../requests';
import {
  SamDefinitionSchema,
  ScoringModelSchema,
  MarketSchema,
} from '../../market';
import {OnboardingTemplateRequestSchema} from '../../onboarding';
import {ScoringSignalSchema} from '../../signals';

// TODO: split these into separate namespaces

export const GetAccountsWithLogos: BatchedApiDefinition<
  {accountIds: ObjectId[]},
  AccountWithLogo[],
  ObjectId,
  AccountWithLogo
> = {
  path: 'accountsWithLogos',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({
    accountIds: ObjectIdSchema.array(),
  }),
  responseDataSchema: AccountWithLogoSchema.array(),
  itemSchema: ObjectIdSchema,
  itemResponseDataSchema: AccountWithLogoSchema,

  createParams: (accountIds) => ({
    accountIds,
  }),
  resolveItem: (accounts, accountId) =>
    accounts.find(({_id}) => _id.equals(accountId)) ?? null,
};

export const ClaimBonusCode = {
  path: 'claimBonusCode',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({
    bonusCode: z.string(),
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const BulkFindAccounts = {
  path: 'bulkFindAccounts',
  method: 'post', // POST instead of GET due to the potential size of the request
  options: {access: 'all'},
  requestDataSchema: z.object({
    domains: z.string().array(),
    type: z.enum(['lookalike', 'preview']),
  }),
  responseDataSchema: ObjectIdSchema.array(),
} satisfies ApiDefinition;

export const ClayExport = {
  path: 'clayExport',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({
    query: z.optional(AccountQuerySchema),
    webhookUrl: z.string(),
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const AccountEnrichmentRedemptionMaxResults = 10;
export const GetAccountEnrichmentRedemptions = {
  path: 'getAccountEnrichmentRedemptions',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({offset: z.number().min(0).optional()}),
  responseDataSchema: z.object({
    totalRedemptions: z.number(),
    redemptions: AccountEnrichmentCreditRedemptionSchema.array(),
  }),
} satisfies ApiDefinition;

export const GetIsEnrichingAgent = {
  path: 'isEnrichingAgent',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    agentId: ObjectIdSchema,
  }),
  responseDataSchema: z.boolean(),
} satisfies ApiDefinition;

export const GetNumAccountsToEnrichDefinition = {
  path: 'numAccountsToEnrich',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    accountSelection: AccountSelectionSchema,
    fieldDefinitionId: ObjectIdSchema,
    enrichmentMode: EnrichmentModeSchema,
    preview: z.boolean().optional(),
  }),
  responseDataSchema: z.object({
    creditsNeeded: z.number(),
    numAccounts: z.number(),
  }),
} satisfies ApiDefinition;

export const RunEnrichmentDefinition = {
  path: 'runEnrichment',
  method: 'post',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    accountSelection: AccountSelectionSchema,
    enrichmentMode: EnrichmentModeSchema,
    fieldDefinitionId: ObjectIdSchema,
    bypassCreditCheck: z.boolean().optional(),
    preview: z.boolean().optional(),
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const GetEnrichedFieldCount = {
  path: 'enrichedFieldCount',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldDefinitionId: ObjectIdSchema,
  }),
  responseDataSchema: z.number(),
} satisfies ApiDefinition;

export const GetLatestFieldDefinitionAnalysisRun = {
  path: 'latestFieldDefinitionAnalysisRun',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldDefinitionId: ObjectIdSchema,
  }),
  responseDataSchema:
    FieldDefinitionAnalysisRunSchema.extend(WithIdSchema).nullable(),
} satisfies ApiDefinition;

export const UpdateFieldDefinitionAnalysisRun = {
  path: 'updateFieldDefinitionAnalysisRun',
  method: 'post',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldDefinitionId: ObjectIdSchema,
    testListIds: ObjectIdSchema.array(),
    bypassCreditCheck: z.boolean().optional(),
  }),
  responseDataSchema:
    FieldDefinitionAnalysisRunSchema.extend(WithIdSchema).nullable(),
} satisfies ApiDefinition;

export const GetFieldDefinitionAnalysisRunResults = {
  path: 'fieldDefinitionAnalysisRunResults',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldDefinitionRunId: ObjectIdSchema,
  }),
  responseDataSchema: z
    .object({
      domain: z.string(),
      testListLabels: z.string().array(),
      value: z.unknown(),
      reasoning: z.string().optional(),
    })
    .array(),
} satisfies ApiDefinition;

export const GetAnalysisRunCredits = {
  path: 'analysisRunCredits',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldDefinitionId: ObjectIdSchema,
    testListIds: ObjectIdSchema.array(),
  }),
  responseDataSchema: z.number(),
} satisfies ApiDefinition;

export const GetEnrichmentPreviewResultDefinition = {
  path: 'enrichmentPreviewResult',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldDefinitionId: ObjectIdSchema,
    accountId: ObjectIdSchema,
  }),
  responseDataSchema: z
    .object({
      value: z.unknown(),
      reasoning: z.string().optional(),
      stale: z.boolean().optional(),
    })
    .nullable(),
} satisfies ApiDefinition;

export const GetTeamSizeParams = {
  path: 'teamSizeParams',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldId: ObjectIdSchema,
  }),
  responseDataSchema: LegacyAiTeamSizeParamsSchema.nullable(),
} satisfies ApiDefinition;

export const GetCreditPrices = {
  path: 'creditPrices',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({}),
  responseDataSchema: CreditPriceResponseSchema,
} satisfies ApiDefinition;

export const CreateCheckoutSession = {
  path: 'createCheckoutSession',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({
    priceId: z.string(),
  }),
  responseDataSchema: z.object({
    url: z.string(),
  }),
} satisfies ApiDefinition;

export const PostMarketRequestSchema = z.object({
  marketId: z.instanceof(ObjectId),
  label: z.string().optional(),
  samDefinition: SamDefinitionSchema.optional(),
  scoringModel: ScoringModelSchema.partial().optional(),
  triggerRefresh: z.boolean().optional(),
});

export const PostMarket: ApiDefinition<
  z.infer<typeof PostMarketRequestSchema>,
  z.infer<typeof MarketSchema>
> = {
  path: 'market',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: PostMarketRequestSchema,
  responseDataSchema: MarketSchema,
};

export const CreateMarket = {
  path: 'createMarket',
  method: 'post',
  options: {access: 'paid'},
  requestDataSchema: z.object({}),
  responseDataSchema: MarketSchema,
} satisfies ApiDefinition;

export const SetOnboardingTemplate = {
  path: 'setOnboardingTemplate',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: OnboardingTemplateRequestSchema,
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const GetSamDefinitionSize = {
  path: 'samDefinitionSize',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    samDefinition: SamDefinitionSchema,
  }),
  responseDataSchema: z.number(),
} satisfies ApiDefinition;

export const GetSamDefinitionWithinLimits = {
  path: 'samDefinitionWithinLimits',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({
    samDefinition: SamDefinitionSchema,
  }),
  responseDataSchema: z.boolean(),
} satisfies ApiDefinition;

export const GetAccountLinks = {
  path: 'accountLinks',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: AccountLinksRequestSchema,
  responseDataSchema: AccountLinkSchema.array(),
} satisfies ApiDefinition;

export const GetAccountSignals = {
  path: 'accountSignals',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({accountId: ObjectIdSchema}),
  responseDataSchema: z.array(ScoringSignalSchema),
} satisfies ApiDefinition;

export const GetAccountSimilarityScore = {
  path: 'accountSimilarityScore',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: AccountSimilarityScoreRequestSchema,
  responseDataSchema: AccountSimilarityScoreResponseSchema,
} satisfies ApiDefinition;

export const GetAccountTypeahead = {
  path: 'accountTypeahead',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({search: z.string()}),
  responseDataSchema: AccountWithLogoSchema.array(),
} satisfies ApiDefinition;

export const GetDownload = {
  path: 'download',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: DownloadRequestSchema,
  responseDataSchema: z.object({url: z.string()}),
} satisfies ApiDefinition;

export const GetMetadata = {
  path: 'metadata',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({}),
  responseDataSchema: z.unknown(),
} satisfies ApiDefinition;

export const GetSignalCounts = {
  path: 'signalCounts',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({
    query: AccountQueryWithoutSortSchema,
  }),
  responseDataSchema: z.record(ScoringSignalSchema, z.number()),
} satisfies ApiDefinition;

export const GetScreenshot = {
  path: 'screenshot',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({id: z.string()}),
  responseDataSchema: z.object({
    root: z.string().nullable(),
    pricing: z.string().nullable(),
  }),
} satisfies ApiDefinition;

export const PostActions = {
  path: 'actions',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.union([
    AccountActionPayloadSchema,
    AccountActionQueryPayloadSchema,
  ]),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const PostExport = {
  path: 'export',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: ExportRequest,
  responseDataSchema: z.object({exportUrl: z.string().optional()}),
} satisfies ApiDefinition;

// TODO: move this const if we request/response types in their own file
export const MaxValidateDomainRows = 5_000;
export const ValidateDomains = {
  path: 'validateDomains',
  method: 'post',
  options: {access: 'admin'},
  requestDataSchema: z.array(
    z
      .object({
        domain: z.string(),
      })
      .catchall(z.unknown())
  ),
  responseDataSchema: z
    .object({
      domain: z.string(),
      finalDomain: z.string().optional(),
      status: z.string(),
    })
    .catchall(z.unknown())
    .array(),
} satisfies ApiDefinition;
